import { DialogRef } from '@angular/cdk/dialog';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TenantKey } from '@app/core/enums/tenant-key.enum';
import { waitForNavigationEnd } from '@domains/auth/components/login-modal/operators/wait-for-login-navigation.operator';
import { ConfirmationModalData } from '@shared/components/confirmation-modal/confirmation-modal.model';
import { ToastService } from '@shared/components/toast';
import {
  CourtService,
  DashboardNavigationService,
  LoaderService,
} from '@shared/services';
import {
  extractErrorMessage,
  getPhoneNumberToPlainNumber,
} from '@shared/utils';
import { tap, finalize, catchError, of } from 'rxjs';
import { objectToSnake } from 'ts-case-convert';
import { CLERK_CREATE_APP_TOAST_CONFIG } from '../../shared/constants/clerk-create-app-toast.constants';
import { getConfirmationData } from '../../shared/helpers/get-confirmation-data.helper';

import { ClerkCreateApplicationHttpService } from '../../shared/services/clerk-create-application-http.service';
import { RegisterTrustFormService } from './register-trust-form.service';
import { FormErrorService } from '@shared/components';
import { TrustOverviewStateService } from '@app/trust/shared/services/trust-overview-state.service';
import { ClerkCreateTrustPayload } from '../../shared/interfaces/clerk-create-trust.interface';

@Injectable({
  providedIn: 'root',
})
export class ClerkCreateTrustFacadeService {
  private readonly _formService = inject(RegisterTrustFormService);
  private readonly _formErrorService = inject(FormErrorService);

  private readonly _toast = inject(ToastService);
  private readonly _router = inject(Router);
  private readonly _clerkCreateApplicationHttpService = inject(
    ClerkCreateApplicationHttpService,
  );
  private readonly _courtService = inject(CourtService);
  private readonly _overviewState = inject(TrustOverviewStateService);
  private readonly _dashboardNavigationService = inject(
    DashboardNavigationService,
  );
  private readonly _loaderService = inject(LoaderService);

  private readonly _dialogRef = inject(DialogRef);

  private readonly _confirmationData: ConfirmationModalData =
    getConfirmationData('G&C');

  private readonly _form = this._formService.form;

  public getConfirmationData(): ConfirmationModalData {
    return this._confirmationData;
  }

  public submitApplication() {
    if (this._form.invalid) return;

    const requestedAction = this._form.controls.requestedAction.value;
    if (!requestedAction) return;

    const rawPayload = this._form.getRawValue();
    const payload: ClerkCreateTrustPayload = objectToSnake({
      email: rawPayload.email,
      isFirm: rawPayload.isFirm,
      firstName: rawPayload.firstName,
      middleName: rawPayload.middleName,
      lastName: rawPayload.lastName,
      username: rawPayload.username,
      userId: rawPayload.userId === 0 ? undefined : rawPayload.userId,
      phoneNumber: getPhoneNumberToPlainNumber(rawPayload.phoneNumber),
      prefixPhone: rawPayload.prefixPhone,
      requestedAction: requestedAction,
      associatedCaseFileNumber: rawPayload.associatedCaseFileNumber,
    });

    this._loaderService.show();
    this._clerkCreateApplicationHttpService
      .createClerkTrust(Number(this._courtService.courtId()), payload)
      .pipe(
        tap(({ trustId }) => {
          this._overviewState.setTrustApplicationId(Number(trustId));
          this._dashboardNavigationService.goToDashboard(TenantKey.TRUST);
        }),
        waitForNavigationEnd(this._router, this._toast),
        tap(() => {
          this._dialogRef.close();
        }),
        finalize(() => {
          this._loaderService.hide();
        }),
        catchError((error) => {
          const errorMessage = extractErrorMessage(error);

          this._formErrorService.displaySimpleErrorModal(
            errorMessage,
            'Error in Register',
          );

          this._toast.show(CLERK_CREATE_APP_TOAST_CONFIG['error']);
          return of(null);
        }),
      )
      .subscribe();
  }
}
